import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getProfile } from "../services/api";
import axios from "axios";
import { RootState } from "./store";
import { UserProfile } from "../types";

// Define types for API specific behavior
export interface UserPreferences {
  theme?: string;
  notifications?: boolean;
  weatherAlerts?: boolean;
  emailFrequency?: "daily" | "weekly" | "monthly" | "none";
  units?: "imperial" | "metric";
}

export interface Location {
  latitude: number;
  longitude: number;
  city?: string;
  name?: string;
  timezone?: string;
}

// Use the imported UserProfile as the type definition

export interface PhotoSubmission {
  id?: string;
  satellite_name: string;
  quality_rating: number;
  notes?: string;
  image_url?: string;
  date_taken?: string;
}

export interface ProfileState {
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  photoSubmitLoading: boolean;
  photoSubmitError: string | null;
  photoSubmitSuccess: string | null;
  photoHistory: PhotoSubmission[];
  photoHistoryLoading: boolean;
  photoHistoryError: string | null;
  lastUpdated: string | null;
}

const initialState: ProfileState = {
  profile: null,
  loading: false,
  error: null,
  photoSubmitLoading: false,
  photoSubmitError: null,
  photoSubmitSuccess: null,
  photoHistory: [],
  photoHistoryLoading: false,
  photoHistoryError: null,
  lastUpdated: null,
};

// Normalize API response to match local UserProfile interface
const normalizeProfileData = (data: any): UserProfile => ({
  id: data.id ?? undefined,
  email: data.email,
  name: data.name ?? undefined,
  tier: data.tier,
  avatar: data.avatar ?? undefined,
  createdAt: data.createdAt ?? undefined,
  updatedAt: data.updatedAt ?? undefined,
  preferences: data.preferences
    ? {
        theme: data.preferences.theme ?? undefined,
        notifications: data.preferences.notifications ?? undefined,
        units: data.preferences.units ?? undefined,
      }
    : undefined,
  location: data.location
    ? {
        latitude: data.location.latitude ?? 0,
        longitude: data.location.longitude ?? 0,
        city: data.location.city ?? undefined,
        name: data.location.name ?? undefined,
        timezone: data.location.timezone ?? undefined,
      }
    : undefined,
  city: data.city ?? undefined,
  latitude: data.latitude ?? undefined,
  longitude: data.longitude ?? undefined,
  trial_end: data.trial_end ?? undefined,
  subscription_start: data.subscription_start ?? undefined,
  referral_code: data.referral_code ?? undefined,
  alert_count: data.alert_count ?? undefined,
  photo_count: data.photo_count ?? undefined,
  photo_submission_count: data.photo_submission_count ?? undefined,
  avg_photo_quality: data.avg_photo_quality ?? undefined,
  subscription: {
    trial_end: data.trial_end ?? undefined,
    start: data.subscription_start ?? undefined,
    status: data.subscription_status ?? 'inactive',
    plan: data.tier ?? 'trial',
  },
  stats: data.stats
    ? {
        simulationsRun: data.stats.simulationsRun ?? undefined,
        photosSubmitted: data.stats.photosSubmitted ?? undefined,
        favoritePasses: data.stats.favoritePasses ?? undefined,
      }
    : undefined,
});

// Create async thunks for better error handling and loading states
export const fetchUserProfile = createAsyncThunk(
  "profile/fetchUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getProfile();
      return normalizeProfileData(data);
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

export const submitPhoto = createAsyncThunk(
  "profile/submitPhoto",
  async (
    photoData: {
      satellite_name: string;
      quality_rating: number;
      notes?: string;
      image_file?: File;
    },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      const formData = new FormData();
      formData.append("satellite_name", photoData.satellite_name);
      formData.append("quality_rating", photoData.quality_rating.toString());

      if (photoData.notes) {
        formData.append("notes", photoData.notes);
      }

      if (photoData.image_file) {
        formData.append("image", photoData.image_file);
      }

      const response = await axios.post("/api/photos", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to submit photo",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

export const fetchPhotoHistory = createAsyncThunk(
  "profile/fetchPhotoHistory",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      const response = await axios.get("/api/photos/history", {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to fetch photo history",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    clearPhotoSubmitStatus(state) {
      state.photoSubmitError = null;
      state.photoSubmitSuccess = null;
    },
    updateTier(state, action: PayloadAction<"trial" | "basic" | "pro">) {
      if (state.profile) {
        state.profile.tier = action.payload;
      }
    },
    updatePreferences(state, action: PayloadAction<Partial<UserPreferences>>) {
      if (state.profile) {
        state.profile.preferences = {
          ...state.profile.preferences,
          ...action.payload,
        };
      }
    },
    updateUserLocation(state, action: PayloadAction<Location>) {
      if (state.profile) {
        state.profile.location = action.payload;
        state.profile.latitude = action.payload.latitude;
        state.profile.longitude = action.payload.longitude;
        state.profile.city = action.payload.city;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(submitPhoto.pending, (state) => {
        state.photoSubmitLoading = true;
        state.photoSubmitError = null;
        state.photoSubmitSuccess = null;
      })
      .addCase(submitPhoto.fulfilled, (state, action) => {
        state.photoSubmitLoading = false;
        state.photoSubmitSuccess =
          action.payload.message || "Photo submitted successfully";
        if (action.payload.photo) {
          state.photoHistory.unshift(action.payload.photo);
        }
      })
      .addCase(submitPhoto.rejected, (state, action) => {
        state.photoSubmitLoading = false;
        state.photoSubmitError = action.payload as string;
      })
      .addCase(fetchPhotoHistory.pending, (state) => {
        state.photoHistoryLoading = true;
        state.photoHistoryError = null;
      })
      .addCase(fetchPhotoHistory.fulfilled, (state, action) => {
        state.photoHistoryLoading = false;
        state.photoHistory = action.payload;
      })
      .addCase(fetchPhotoHistory.rejected, (state, action) => {
        state.photoHistoryLoading = false;
        state.photoHistoryError = action.payload as string;
      });
  },
});

export const {
  clearPhotoSubmitStatus,
  updateTier,
  updatePreferences,
  updateUserLocation,
} = profileSlice.actions;

// Selectors
export const selectProfile = (state: RootState) => state.profile.profile;
export const selectProfileLoading = (state: RootState) => state.profile.loading;
export const selectProfileError = (state: RootState) => state.profile.error;
export const selectUserTier = (state: RootState) => state.profile.profile?.tier;
export const selectIsProUser = (state: RootState) =>
  state.profile.profile?.tier === "pro";
export const selectPhotoHistory = (state: RootState) =>
  state.profile.photoHistory;

export default profileSlice.reducer;
